import React, { useEffect, useState } from 'react';
import './App.css';
import '../src/styles/feedback.css';
import { OfflineIndicator } from './components/OfflineIndicator';
import { ResponsiveLayout, ResponsiveStack, ResponsiveButton, NotificationContainer, ErrorBoundary } from './components';
import { GameApp } from './components/GameApp';
import { usePWA } from './hooks/usePWA';
import { useErrorHandling } from './hooks/useErrorHandling';
import { ErrorType, ErrorSeverity } from './services/ErrorHandlingService';
import { SecurityService } from './services/SecurityService';
import { TokenEncryptionService } from './services/TokenEncryptionService';

function App() {
  const { 
    capabilities, 
    updateAvailable, 
    install, 
    applyUpdate, 
    getLaunchMode, 
    updateThemeColor 
  } = usePWA();
  
  const [errorState, errorActions] = useErrorHandling('App');
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [installPromptDismissed, setInstallPromptDismissed] = useState(false);
  const [launchMode, setLaunchMode] = useState<string | null>(null);

  // Initialize security services
  useEffect(() => {
    const initializeSecurity = async () => {
      try {
        // Initialize HTTPS enforcement and security headers
        const securityService = SecurityService.getInstance();
        securityService.initialize();
        
        // Initialize token encryption
        const encryptionService = TokenEncryptionService.getInstance();
        if (encryptionService.isAvailable()) {
          await encryptionService.initialize();
        } else {
          console.warn('⚠️ Token encryption not available in this browser');
        }
        
        // Log security status
        const securityStatus = securityService.getSecurityStatus();
        console.log('🔒 Security Status:', securityStatus);
      } catch (error) {
        console.error('Failed to initialize security services:', error);
      }
    };
    
    initializeSecurity();
  }, []);

  useEffect(() => {
    // Check launch mode on app start
    const mode = getLaunchMode();
    setLaunchMode(mode);

    // Check if user has previously dismissed the install prompt
    const dismissed = localStorage.getItem('installPromptDismissed');
    if (dismissed === 'true') {
      setInstallPromptDismissed(true);
    }

    // Show install prompt if app is installable (but not immediately and not if dismissed)
    if (capabilities.isInstallable && !capabilities.isInstalled && dismissed !== 'true') {
      // Delay showing install prompt significantly to not interfere with initial experience
      // Only show after user has had time to explore the app (30 seconds)
      const showTimer = setTimeout(() => {
        setShowInstallPrompt(true);
        
        // Auto-hide after 10 seconds if user doesn't interact
        const hideTimer = setTimeout(() => {
          setShowInstallPrompt(false);
        }, 10000);
        
        // Store the timer so we can clear it if user interacts
        (window as any).installPromptHideTimer = hideTimer;
      }, 30000);
      
      // Cleanup timer on unmount
      return () => {
        clearTimeout(showTimer);
        if ((window as any).installPromptHideTimer) {
          clearTimeout((window as any).installPromptHideTimer);
        }
      };
    }

    // Update theme color based on app theme
    updateThemeColor('#4A90E2');

    // Handle launch mode actions
    if (mode) {
      handleLaunchMode(mode);
    }

    // Setup PWA-specific behaviors
    setupPWABehaviors();
  }, [capabilities, getLaunchMode, updateThemeColor]);

  const setupPWABehaviors = () => {
    // Add app-like touch feedback to interactive elements
    const interactiveElements = document.querySelectorAll('button, .button, [role="button"]');
    interactiveElements.forEach(element => {
      element.classList.add('touch-feedback');
    });

    // Handle keyboard navigation for better accessibility
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        // Handle escape key for modal dismissal, etc.
        setShowInstallPrompt(false);
      }
    });
  };

  const handleLaunchMode = (mode: string) => {
    // Handle different launch modes from shortcuts
    switch (mode) {
      case 'single':
        console.log('Launching single player mode');
        setLaunchMode('single');
        break;
      case 'multi':
        console.log('Launching multiplayer mode');
        setLaunchMode('multi');
        break;
      case 'stats':
        console.log('Launching statistics view');
        setLaunchMode('stats');
        break;
      case 'join':
        console.log('Launching join game flow');
        setLaunchMode('join');
        break;
      default:
        console.log('Default launch');
    }
  };

  const handleInstall = async () => {
    // Clear auto-hide timer
    if ((window as any).installPromptHideTimer) {
      clearTimeout((window as any).installPromptHideTimer);
    }
    
    try {
      const success = await install();
      if (success) {
        setShowInstallPrompt(false);
        localStorage.setItem('installPromptDismissed', 'true');
      }
    } catch (error) {
      errorActions.handleError(
        error instanceof Error ? error : new Error('Failed to install app'),
        ErrorType.SYSTEM,
        ErrorSeverity.MEDIUM,
        { action: 'install_app' }
      );
    }
  };

  const handleDismissInstallPrompt = (permanent = false) => {
    // Clear auto-hide timer
    if ((window as any).installPromptHideTimer) {
      clearTimeout((window as any).installPromptHideTimer);
    }
    
    setShowInstallPrompt(false);
    if (permanent) {
      localStorage.setItem('installPromptDismissed', 'true');
      setInstallPromptDismissed(true);
    } else {
      // Show again after 24 hours if not permanently dismissed
      setTimeout(() => {
        if (!installPromptDismissed && capabilities.isInstallable && !capabilities.isInstalled) {
          setShowInstallPrompt(true);
        }
      }, 24 * 60 * 60 * 1000); // 24 hours
    }
  };

  const handleUpdate = () => {
    applyUpdate();
  };

  const getWelcomeMessage = () => {
    if (launchMode === 'single') {
      return 'Ready for Single Player!';
    }
    if (launchMode === 'multi') {
      return 'Ready for Multiplayer!';
    }
    if (capabilities.isStandalone) {
      return 'Welcome to Tic-Tac-Toe PWA!';
    }
    return 'Progressive Web App Tic-Tac-Toe Game';
  };

  return (
    <ErrorBoundary componentName="App">
      <div className="App">
        <OfflineIndicator />
        <NotificationContainer position="top-right" />
      
      {/* Update Available Banner */}
      {updateAvailable && (
        <div className="update-banner">
          <span>New version available!</span>
          <ResponsiveButton 
            variant="outline" 
            size="small" 
            onClick={handleUpdate}
          >
            Update
          </ResponsiveButton>
        </div>
      )}

      {/* Subtle Install Prompt */}
      {showInstallPrompt && !installPromptDismissed && (
        <div className="install-prompt-subtle">
          <div className="install-prompt-content">
            <span className="install-icon">📱</span>
            <span className="install-text">Add to Home Screen</span>
            <div className="install-actions">
              <button 
                className="install-btn-install"
                onClick={handleInstall}
                aria-label="Install app"
              >
                Install
              </button>
              <button 
                className="install-btn-later"
                onClick={() => handleDismissInstallPrompt(false)}
                aria-label="Maybe later"
              >
                Later
              </button>
              <button 
                className="install-btn-close"
                onClick={() => handleDismissInstallPrompt(true)}
                aria-label="Don't show again"
              >
                ×
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PWA Status Header */}
      {capabilities.isStandalone && (
        <div className="pwa-status-bar">
          <span>📱 Running as App</span>
          {capabilities.isInstalled && <span> • Installed</span>}
        </div>
      )}

      {/* Main Game Application */}
      <GameApp onLaunchMode={handleLaunchMode} />
    </div>
    </ErrorBoundary>
  );
}

export default App;